import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import DocumentViewer from './DocumentViewer';
import DocumentPhoto from './DocumentPhoto';
import { Button } from '../MUIs';
import Global from '../../Services/Common/Global';
import { useSelector } from 'react-redux';

export default function DocumentPreview({
  open,
  onClose,
  documentData,
  onConfirm,
  caseType = 'file', // file: filePath 있으면 기존 로직, 없으면 PrintLogic 실행 / report: 바로 미리보기 / other: 해당 페이지만의 특별한 로직
  onPrintLogic, // file 타입에서 filePath 없을 때 실행할 함수
  onOtherLogic, // other 타입에서 실행할 함수
}) {
  const [renderPreview, setRenderPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(window.innerWidth * 0.9);
  const [pageViewHeight, setPageViewHeight] = useState(0);
  const [isImageFile, setIsImageFile] = useState(false);
  const { statusBar, navBar } = useSelector((state) => state.systemBar);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (open && documentData) {
      LoadDocument();
    } else {
      CleanupPreview();
    }

    const handleResize = () => {
      setPageWidth(window.innerWidth * 0.9);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      CleanupPreview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentData]);

  // 미리보기 메모리 초기화
  const CleanupPreview = () => {
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch (e) {
        // ignore
      }
      objectUrlRef.current = null;
    }
    setRenderPreview(null);
    setPageNumber(1);
    setNumPages(0);
    setIsImageFile(false);
    setError(null);
  };

  // Blob URL 설정 (report / other 타입 전용)
  const SetObjectUrlPreview = (blob) => {
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch (e) {
        // ignore
      }
    }
    const blobUrl = URL.createObjectURL(blob);
    objectUrlRef.current = blobUrl;
    setRenderPreview(blobUrl);
  };

  const LoadDocument = async () => {
    setError(null);

    if (caseType === 'file') {
      const hasFilePath =
        documentData?.FILE_PATH ||
        documentData?.FILE_URL ||
        documentData?.filePath;

      // filePath 없고 PrintLogic 있으면 비동기 blob 로드
      if (!hasFilePath && onPrintLogic && typeof onPrintLogic === 'function') {
        setLoading(true);
        try {
          const blob = await onPrintLogic(documentData);
          if (blob) {
            SetObjectUrlPreview(blob);
            setIsImageFile(false);
          } else {
            setError('문서를 불러올 수 없습니다.');
          }
        } catch (err) {
          console.error('PrintLogic 실행 실패:', err);
          setError('문서를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (!hasFilePath) {
        setError('미리볼 파일이 없습니다.');
        return;
      }

      // filePath가 있는 경우: fetch/Blob 없이 URL을 직접 전달
      // → react-pdf / img 태그가 스트리밍·Range 요청으로 처리해 메모리 절약
      const filePath =
        documentData.FILE_PATH ||
        documentData.FILE_URL ||
        documentData.filePath;

      const imageExtensions = ['jpg', 'jpeg', 'png'];
      const fileExtension = filePath.split('.').pop()?.toLowerCase() || '';
      const isImage = imageExtensions.includes(fileExtension);
      setIsImageFile(isImage);

      const previewFilePath = isImage
        ? filePath
        : filePath.replace(/\.[^/.]+$/, '.pdf');
      const fullUrl = previewFilePath.startsWith('http')
        ? previewFilePath
        : Global.file.useUrl + 'files/' + previewFilePath;

      setRenderPreview(fullUrl);
      return;
    }

    // report / other 타입: blob 비동기 로드
    setLoading(true);
    try {
      if (caseType === 'report') {
        if (onPrintLogic && typeof onPrintLogic === 'function') {
          const blob = await onPrintLogic(documentData);
          if (blob) {
            SetObjectUrlPreview(blob);
            setIsImageFile(false);
          } else {
            setError('문서를 불러올 수 없습니다.');
          }
        } else {
          setError('문서 출력 함수가 제공되지 않았습니다.');
        }
      } else if (caseType === 'other') {
        if (documentData?.blob) {
          SetObjectUrlPreview(documentData.blob);
          setIsImageFile(documentData.blob.type?.startsWith('image/'));
          return;
        }
        if (onOtherLogic && typeof onOtherLogic === 'function') {
          const printItem = documentData?.printItem || null;
          const blob = await onOtherLogic(documentData, printItem);
          if (blob) {
            SetObjectUrlPreview(blob);
            setIsImageFile(blob.type?.startsWith('image/'));
          } else {
            setError('문서를 불러올 수 없습니다.');
          }
        } else {
          setError('문서 처리 함수가 제공되지 않았습니다.');
        }
      }
    } catch (err) {
      console.error('문서 로드 실패:', err);
      setError('문서를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const HandleDocumentError = () => {
    setError('문서를 열 수 없습니다. 파일이 존재하지 않거나 손상되었을 수 있습니다.');
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const HandleClose = () => {
    CleanupPreview();
    if (typeof onClose === 'function') onClose();
  };

  const renderContent = () => {
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
            height: '100%',
          }}
        >
          <Typography sx={{ color: '#f44336', textAlign: 'center', fontSize: '15px' }}>
            {error}
          </Typography>
          <Button name="닫기" onClick={HandleClose} sx={{ minWidth: 120 }} />
        </Box>
      );
    }

    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            height: '100%',
          }}
        >
          <CircularProgress size={50} thickness={6} />
          <Typography sx={{ color: '#4b4949ff' }}>문서를 불러오는 중...</Typography>
        </Box>
      );
    }

    if (renderPreview) {
      if (isImageFile) {
        return (
          <DocumentPhoto
            renderPreview={renderPreview}
            loading={loading}
            setLoading={setLoading}
            documentData={documentData}
          />
        );
      }
      return (
        <DocumentViewer
          renderPreview={renderPreview}
          loading={loading}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          pageNumber={pageNumber}
          pageWidth={pageWidth}
          pageViewHeight={pageViewHeight}
          setPageViewHeight={setPageViewHeight}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onError={HandleDocumentError}
        />
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={HandleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          m: 0,
          pt: `${statusBar}px`,
          pb: `${navBar}px`,
          borderRadius: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#fff',
        }}
      >
        {/* 제목 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {documentData?.FILE_NM || documentData?.fileNm || documentData?.TITLE || '문서 미리보기'}
          </Typography>
          <IconButton onClick={HandleClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 본문 */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: error || loading ? 2 : 0,
          }}
        >
          {renderContent()}
        </Box>

        {/* 이전/다음 버튼 */}
        {!error && numPages > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button
              name="이전"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
            />
            <Typography>
              {pageNumber} / {numPages}
            </Typography>
            <Button
              name="다음"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
            />
          </Box>
        )}

        {/* 확인 버튼 */}
        {!error && onConfirm && (
          <Box
            sx={{
              p: 2,
              borderTop: numPages > 0 ? '1px solid #e0e0e0' : 'none',
            }}
          >
            <Button name="확인" onClick={onConfirm} fullWidth />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
